import * as Cesium from 'cesium';
import {
  APP_CONFIG,
  QUALITY_SETTINGS,
  type VisualSettings,
  type QualityMode,
  visualSettingsForQuality,
} from '../config/appConfig';

export function applyVisualSettings(
  viewer: Cesium.Viewer,
  settings: VisualSettings,
  tilesets: Cesium.Cesium3DTileset[] = [],
): void {
  const { scene } = viewer;

  // 地球の照明は影の計算にも使うため常に有効にし、大気だけを個別に表示切替する。
  scene.globe.enableLighting = true;
  scene.globe.dynamicAtmosphereLighting = settings.atmosphere;
  scene.globe.dynamicAtmosphereLightingFromSun = settings.atmosphere;
  scene.globe.showGroundAtmosphere = settings.atmosphere;
  if (scene.skyAtmosphere) scene.skyAtmosphere.show = settings.atmosphere;

  scene.fog.enabled = settings.fog;
  scene.fog.density = settings.fogDensity;
  scene.fog.screenSpaceErrorFactor = settings.fog ? 1 : 0;
  scene.highDynamicRange = settings.hdr && scene.highDynamicRangeSupported;
  viewer.shadows = settings.shadows;
  viewer.terrainShadows = settings.shadows
    ? Cesium.ShadowMode.ENABLED
    : Cesium.ShadowMode.DISABLED;

  for (const tileset of tilesets) {
    tileset.shadows = settings.shadows
      ? Cesium.ShadowMode.ENABLED
      : Cesium.ShadowMode.DISABLED;
  }

  scene.requestRender();
}

export function applyQuality(
  viewer: Cesium.Viewer,
  quality: QualityMode,
  tilesets: Cesium.Cesium3DTileset[] = [],
): void {
  const settings = QUALITY_SETTINGS[quality];
  viewer.scene.requestRenderMode = true;
  viewer.scene.maximumRenderTimeChange = Number.POSITIVE_INFINITY;
  viewer.scene.globe.maximumScreenSpaceError =
    settings.terrainMaximumScreenSpaceError;
  viewer.scene.globe.tileCacheSize = settings.terrainTileCacheSize;
  viewer.resolutionScale = settings.resolutionScale;

  applyVisualSettings(viewer, visualSettingsForQuality(quality), tilesets);

  for (const tileset of tilesets) {
    tileset.maximumScreenSpaceError = settings.buildingMaximumScreenSpaceError;
  }

  viewer.scene.requestRender();
}

export function configureEcoMode(viewer: Cesium.Viewer): void {
  applyQuality(viewer, APP_CONFIG.defaultQuality);
}

export function buildingTilesetOptions(): Cesium.Cesium3DTileset.ConstructorOptions {
  const settings = QUALITY_SETTINGS[APP_CONFIG.defaultQuality];
  return {
    maximumScreenSpaceError: settings.buildingMaximumScreenSpaceError,
    cacheBytes: settings.buildingCacheBytes,
    maximumCacheOverflowBytes: settings.buildingMaximumCacheOverflowBytes,
    dynamicScreenSpaceError: true,
    dynamicScreenSpaceErrorDensity: 2.0e-4,
    dynamicScreenSpaceErrorFactor: 24,
    cullRequestsWhileMoving: true,
    cullRequestsWhileMovingMultiplier: 60,
    preloadWhenHidden: false,
    preloadFlightDestinations: true,
    preferLeaves: false,
    foveatedScreenSpaceError: true,
    foveatedTimeDelay: 0.2,
    skipLevelOfDetail: true,
    baseScreenSpaceError: 1024,
    skipScreenSpaceErrorFactor: 16,
    skipLevels: 1,
    immediatelyLoadDesiredLevelOfDetail: false,
    loadSiblings: false,
  };
}
